import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useLanguage } from '../i18n/LanguageContext';

const API_BASE_URL = (process.env as any).REACT_APP_API_URL || 'http://localhost:8000';

type StakeData = {
  id: number;
  amount: number;
  daily_rate: number;
  start_date: string;
  unlock_date: string;
  days_remaining: number;
  pending_rewards: number;
  is_unlocked: boolean;
  can_claim: boolean;
  hours_until_claim: number;
  next_claim_at: string;
  expires_at: string;
  hours_until_burn: number;
  will_burn_soon: boolean;
  status: string;
};

const StakeScreen = () => {
  const { t } = useLanguage();
  const userId = (window as any)?.Telegram?.WebApp?.initDataUnsafe?.user?.id?.toString() || 'demo';
  
  const [balance, setBalance] = useState<number>(0);
  const [stakeAmount, setStakeAmount] = useState<string>('');
  const [currentStake, setCurrentStake] = useState<StakeData | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<string>('');
  const [timeUntilClaim, setTimeUntilClaim] = useState<string>('');
  const [isGoldTreasureUnlocked, setIsGoldTreasureUnlocked] = useState<boolean>(false);

  // Функция для форматирования времени до claim в формате чч:мм:сс
  const formatTimeUntilClaim = (nextClaimAt: string): string => {
    const now = new Date();
    const claimTime = new Date(nextClaimAt);
    const diff = claimTime.getTime() - now.getTime();
    
    if (diff <= 0) return '00:00:00';
    
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((diff % (1000 * 60)) / 1000);
    
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  };

  useEffect(() => {
    loadData();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Обновление таймера каждую секунду
  useEffect(() => {
    if (currentStake && !currentStake.can_claim && currentStake.next_claim_at) {
      const interval = setInterval(() => {
        setTimeUntilClaim(formatTimeUntilClaim(currentStake.next_claim_at));
      }, 1000);
      
      // Инициализация при первой загрузке
      setTimeUntilClaim(formatTimeUntilClaim(currentStake.next_claim_at));
      
      return () => clearInterval(interval);
    }
  }, [currentStake]);

  const loadData = async (retryCount = 3): Promise<void> => {
    try {
      console.log('📡 Загрузка данных стейка...', { userId, retryCount });
      
      // Загружаем баланс
      const balanceRes = await axios.get(`${API_BASE_URL}/user/balance`, {
        params: { wallet_address: userId },
        timeout: 30000
      });
      setBalance(balanceRes.data.balance || 0);

      // Загружаем текущий stake
      const stakeRes = await axios.get(`${API_BASE_URL}/stake`, {
        params: { wallet_address: userId },
        timeout: 30000
      });
      console.log('✅ Данные стейка загружены:', stakeRes.data);
      setCurrentStake(stakeRes.data.stake || null);
      
      // Проверяем разблокирован ли Gold Treasure (сундук 3)
      const packagesRes = await axios.get(`${API_BASE_URL}/packages`, {
        params: { wallet_address: userId },
        timeout: 30000
      });
      const goldTreasure = packagesRes.data.find((p: any) => p.id === 3);
      setIsGoldTreasureUnlocked(goldTreasure?.is_unlocked || false);
    } catch (err: any) {
      console.error('❌ Ошибка загрузки данных стейка:', {
        message: err.message,
        code: err.code,
        response: err.response?.data,
        retryCount
      });
      
      // Повторная попытка при таймауте или сетевых ошибках
      if (retryCount > 0 && (err.code === 'ECONNABORTED' || err.code === 'ERR_NETWORK' || !err.response)) {
        console.log(`🔄 Повторная попытка через 2 сек... (осталось попыток: ${retryCount})`);
        await new Promise(resolve => setTimeout(resolve, 2000));
        return loadData(retryCount - 1);
      }
      
      // Не показываем ошибку пользователю если просто нет stake
      if (err.response?.status !== 404) {
        setError('Ошибка загрузки данных');
      }
    }
  };

  const handleCreateStake = async () => {
    const amount = parseFloat(stakeAmount);
    
    if (isNaN(amount) || amount < 0.7) {
      setError('Минимальная сумма стейка: 0.7 TON');
      return;
    }

    if (amount > balance) {
      setError('Недостаточно средств на балансе');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      // Получаем referrer_id из localStorage (если есть)
      const referrerId = localStorage.getItem('referrer_id');
      const requestData: any = {
        wallet_address: userId,
        amount: amount
      };
      if (referrerId) {
        requestData.referrer_id = parseInt(referrerId);
        console.log('🔗 Передаем referrer_id в stake:', referrerId);
      }
      
      const response = await axios.post(`${API_BASE_URL}/stake`, requestData, { timeout: 30000 });

      // Проверяем был ли stake создан или увеличен
      if (response.data.message.includes('increased')) {
        setSuccess(t.stakeIncreased);
      } else {
        setSuccess(t.stakeCreated);
      }
      
      setStakeAmount('');
      await loadData();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Ошибка создания стейка');
    } finally {
      setLoading(false);
    }
  };

  const handleClaimRewards = async () => {
    if (!currentStake || currentStake.pending_rewards <= 0) {
      setError('Нет доступных наград для сбора');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const response = await axios.post(`${API_BASE_URL}/stake/claim`, {
        wallet_address: userId
      }, { timeout: 30000 });

      setSuccess(response.data.message);
      await loadData();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Ошибка при сборе наград');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="pb-20 px-3 min-h-screen bg-gradient-to-br from-indigo-500 to-purple-600">
      <div className="text-white space-y-4 pt-4">
        {/* Header */}
        <div className="card bg-gradient-to-br from-indigo-700/80 to-purple-800/80 text-white text-center">
          <div className="text-3xl font-bold mb-2">{t.stakeTitle}</div>
          <div className="text-sm opacity-90">{t.stakeDescription}</div>
        </div>

        {/* Available Balance */}
        <div className="card bg-gradient-to-br from-indigo-700/80 to-purple-800/80 text-white">
          <div className="text-sm opacity-80">{t.availableBalance}</div>
          <div className="text-2xl font-bold">{balance.toFixed(6)} TON</div>
        </div>

        {/* Current Stake Info */}
        {currentStake ? (
          <div className="card bg-gradient-to-br from-indigo-700/80 to-purple-800/80 text-white">
            <div className="text-xl font-bold mb-3 text-center">
              {currentStake.is_unlocked ? t.stakeUnlocked : t.activeStake}
            </div>
            
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="opacity-70">{t.stakedAmount}</span>
                <span className="font-bold">{currentStake.amount} TON</span>
              </div>
              
              <div className="flex justify-between">
                <span className="opacity-70">{t.dailyRate}</span>
                <span className="font-bold">{currentStake.daily_rate}%</span>
              </div>
              
              <div className="flex justify-between">
                <span className="opacity-70">{t.daysRemaining}</span>
                <span className="font-bold">
                  {currentStake.is_unlocked ? '✅ Ready!' : `${currentStake.days_remaining} ${t.days || 'дн.'}`}
                </span>
              </div>
              
              <div className="flex justify-between text-green-400">
                <span>{t.pendingRewards}</span>
                <span className="font-bold">{currentStake.pending_rewards.toFixed(6)} TON</span>
              </div>
            </div>

            {/* Claim availability info */}
            {!currentStake.can_claim && currentStake.hours_until_claim > 0 && (
              <div className="mt-3 p-3 bg-yellow-500/20 border border-yellow-500/50 rounded-lg text-center">
                <div className="text-sm font-semibold">{t.claimAvailableIn}</div>
                <div className="text-2xl font-bold font-mono tracking-wider">
                  {timeUntilClaim || '00:00:00'}
                </div>
                <div className="text-xs mt-1 opacity-80">{t.canClaimOncePerDay}</div>
              </div>
            )}

            {/* Warning about burning */}
            {currentStake.can_claim && currentStake.will_burn_soon && (
              <div className="mt-3 p-3 bg-red-600/30 border-2 border-red-500 rounded-lg text-center animate-pulse">
                <div className="text-lg font-bold text-red-200">🔥 {t.burnWarningTitle || 'ВНИМАНИЕ! СЖИГАНИЕ!'}</div>
                <div className="text-sm mt-1">
                  {t.burnWarningText || `Осталось ${Math.floor(currentStake.hours_until_burn)}ч ${Math.floor((currentStake.hours_until_burn % 1) * 60)}м до сжигания!`}
                </div>
                <div className="text-xs mt-1 opacity-90">
                  {t.burnWarningInfo || 'Если не собрать сейчас - получите только 70% (30% сгорит)'}
                </div>
              </div>
            )}

            {/* Claim Button */}
            <button
              onClick={handleClaimRewards}
              disabled={loading || !currentStake.can_claim}
              className={`w-full mt-4 py-3 rounded-lg font-bold text-lg transition-all ${
                currentStake.can_claim && !loading
                  ? 'bg-green-600 hover:bg-green-700 text-white'
                  : 'bg-gray-500 text-gray-300 cursor-not-allowed'
              }`}
            >
              {loading ? (t.processing || 'Processing...') : `${t.claimButton} ${currentStake.pending_rewards.toFixed(6)} TON`}
            </button>

            {currentStake.is_unlocked && (
              <div className="mt-3 text-center text-sm text-green-400">
                {t.principalReturned}
              </div>
            )}
            
            {/* Форма для увеличения stake */}
            <div className="card bg-gradient-to-br from-indigo-700/80 to-purple-800/80 text-white mt-4">
              <div className="text-lg font-bold mb-2">{t.orIncreaseStake}</div>
              
              <div className="mb-3">
                <label className="block text-sm mb-2 opacity-70">{t.amountLabel}</label>
                <input
                  type="number"
                  placeholder={t.minimumStake}
                  value={stakeAmount}
                  onChange={(e) => setStakeAmount(e.target.value)}
                  className="w-full px-4 py-3 rounded-lg bg-white/10 border border-white/20 text-white placeholder-white/50"
                  min="0.7"
                  step="0.1"
                />
              </div>

              <button
                onClick={handleCreateStake}
                disabled={loading || !stakeAmount || parseFloat(stakeAmount) < 0.7}
                className={`w-full py-3 rounded-lg font-bold transition-all ${
                  !loading && stakeAmount && parseFloat(stakeAmount) >= 0.7
                    ? 'bg-blue-600 hover:bg-blue-700 text-white'
                    : 'bg-gray-500 text-gray-300 cursor-not-allowed'
                }`}
              >
                {loading ? (t.processing || 'Processing...') : t.increaseStakeButton}
              </button>
            </div>
          </div>
        ) : !isGoldTreasureUnlocked ? (
          // Stake заблокирован - нужно разблокировать Gold Treasure
          <div className="card bg-gradient-to-br from-indigo-700/80 to-purple-800/80 text-white text-center">
            <div className="text-5xl mb-4">🔒</div>
            <div className="text-xl font-bold mb-2">{t.stakeTitle}</div>
            <div className="text-lg opacity-90">{t.stakeLockedMessage}</div>
          </div>
        ) : (
          <div className="card bg-gradient-to-br from-indigo-700/80 to-purple-800/80 text-white">
            <div className="text-lg font-bold mb-3">{t.createNewStake}</div>
            
            <div className="mb-4">
              <label className="block text-sm mb-2 opacity-70">{t.amountLabel}</label>
              <input
                type="number"
                placeholder={t.minimumStake}
                value={stakeAmount}
                onChange={(e) => setStakeAmount(e.target.value)}
                className="w-full px-4 py-3 rounded-lg bg-white/10 border border-white/20 text-white placeholder-white/50"
                min="0.7"
                step="0.1"
              />
            </div>

            <div className="text-sm opacity-70 mb-4 space-y-1">
              <div>✅ {t.lockPeriod}</div>
              <div>✅ {t.dailyReturn}</div>
              <div>✅ {t.totalReturn}</div>
              <div>✅ {t.claimAnytime}</div>
            </div>

            <button
              onClick={handleCreateStake}
              disabled={loading || !stakeAmount || parseFloat(stakeAmount) < 0.7}
              className={`w-full py-3 rounded-lg font-bold transition-all ${
                !loading && stakeAmount && parseFloat(stakeAmount) >= 0.7
                  ? 'bg-purple-600 hover:bg-purple-700 text-white'
                  : 'bg-gray-500 text-gray-300 cursor-not-allowed'
              }`}
            >
              {loading ? (t.processing || 'Processing...') : t.createStakeButton}
            </button>
          </div>
        )}

        {/* Notifications */}
        {error && (
          <div className="card bg-red-500/20 border border-red-500 text-white">
            <div className="font-bold">❌ {t.error || 'Error'}</div>
            <div className="text-sm">{error}</div>
          </div>
        )}

        {success && (
          <div className="card bg-green-500/20 border border-green-500 text-white">
            <div className="font-bold">✅ {t.success || 'Success'}</div>
            <div className="text-sm">{success}</div>
          </div>
        )}

        {/* Info Card */}
        <div className="card bg-gradient-to-br from-indigo-700/80 to-purple-800/80 text-white">
          <div className="text-lg font-bold mb-2">{t.howItWorksStake}</div>
          <div className="text-sm opacity-80 space-y-2">
            <p>{t.howItWorksText1}</p>
            <p>{t.howItWorksText2}</p>
            <p>{t.howItWorksText3}</p>
            <p>{t.howItWorksText4}</p>
            <div className="mt-3 p-3 bg-red-900/40 border border-red-500/50 rounded-lg">
              <p className="font-semibold text-red-300">{t.howItWorksText5}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StakeScreen;

